import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * BROS Paid Re-entry (Diagnostics card): BR OS's own "paid" status is an
 * internal auto-match flag whose evidence (which bank transaction, what
 * reference) never reaches Finance Agent -- only a bare boolean does. Before
 * this feature that boolean alone permanently excluded the invoice from the
 * match-candidate pool, so Finance Agent never attempted its own
 * reconciliation and the invoice sat "Not reconciled" forever.
 *
 * Two-step like Stale Suggested Matches Sweep: Preview (never mutates) then
 * Apply (arms the population change for the NEXT auto-match chain run --
 * creates no matches itself). Once active, the card switches to showing the
 * ongoing per-run tallies instead of the preview.
 */
export function BrosPaidReentryPanel() {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [tallies, setTallies] = useState(null);
  const [talliesLoading, setTalliesLoading] = useState(false);

  const fetchTallies = useCallback(async () => {
    try {
      const { data } = await matchingAPI.brosPaidReentryTallies();
      setTallies(data);
      return data;
    } catch (err) {
      toast.error(getErrorMessage(err));
      return null;
    }
  }, []);

  const refreshTallies = async () => {
    setTalliesLoading(true);
    try {
      await fetchTallies();
    } finally {
      setTalliesLoading(false);
    }
  };

  useEffect(() => { fetchTallies(); }, [fetchTallies]);

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await matchingAPI.previewBrosPaidReentry();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Arm BROS paid re-entry? ${preview?.reentry_count || 0} invoice(s) will re-enter the ` +
      'match-candidate pool starting with the next auto-match chain run. This does not create ' +
      'any match itself -- it only changes what the next run considers.'
    )) return;
    setApplyLoading(true);
    try {
      await matchingAPI.applyBrosPaidReentry();
      await fetchTallies();
      setPreview(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyLoading(false);
    }
  };

  const active = tallies?.active;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">BROS Paid Re-entry</CardTitle>
          {active != null && (
            <Badge variant={active ? 'default' : 'outline'} data-testid="bros-paid-reentry-status">
              {active ? 'Active' : 'Not activated'}
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          BR OS's own "paid" status is an internal auto-match flag whose evidence never reaches
          Finance Agent -- only a bare boolean does. A BROS-source paid invoice with no
          Finance-Agent-confirmed payment re-enters the match-candidate pool so this system does
          its own reconciliation instead of taking BR OS's word for it. First activation: Preview,
          then Apply. Never auto-confirms -- re-entered invoices confirm via the normal
          learned-pattern path like everything else.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!active && (
          <>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="bros-paid-reentry-preview">
                {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Preview
              </Button>
              {preview && !preview.error && preview.reentry_count > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={runApply}
                  disabled={applyLoading}
                  data-testid="bros-paid-reentry-apply"
                >
                  {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Apply to {preview.reentry_count} Invoices
                </Button>
              )}
            </div>

            {preview && (
              <div className="space-y-2" data-testid="bros-paid-reentry-preview-result">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>Scanned: <strong>{preview.population_scanned}</strong></span>
                  <span>Would re-enter: <strong>{preview.reentry_count}</strong></span>
                  <span>
                    Total amount:{' '}
                    <strong>
                      {Object.entries(preview.reentry_total_by_currency || {})
                        .map(([cur, amt]) => `${amt} ${cur}`)
                        .join(' · ') || '0'}
                    </strong>
                  </span>
                  {preview.excluded_pre_scope_count > 0 && (
                    <span className="text-amber-600">
                      {preview.excluded_pre_scope_count} excluded (pre-scope)
                    </span>
                  )}
                  {preview.truncated && (
                    <span className="text-amber-600">Truncated at scan cap</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{preview.message}</p>
                {preview.sample?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-1.5">Invoice</th>
                          <th className="text-left p-1.5">Client/Supplier</th>
                          <th className="text-left p-1.5">Amount</th>
                          <th className="text-left p-1.5">Issue date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sample.map((row) => (
                          <tr key={row.id} className="border-t" data-testid="bros-paid-reentry-sample-row">
                            <td className="p-1.5 font-mono">{row.invoice_number || row.id}</td>
                            <td className="p-1.5">{row.supplier_client_name || '—'}</td>
                            <td className="p-1.5 font-mono">{row.amount} {row.currency}</td>
                            <td className="p-1.5 font-mono">{row.issue_date || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {active && (
          <div className="space-y-3" data-testid="bros-paid-reentry-tallies">
            <div className="flex items-center gap-2">
              <Button onClick={refreshTallies} disabled={talliesLoading} size="sm" variant="outline" data-testid="bros-paid-reentry-tallies-refresh">
                {talliesLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Refresh
              </Button>
              <span className="text-xs text-muted-foreground">
                Activated {tallies.activated_at} by {tallies.activated_by}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="border rounded p-2">
                <div className="text-muted-foreground text-xs">Candidates seen</div>
                <div className="font-mono text-lg">{tallies.candidates_seen}</div>
              </div>
              <div className="border rounded p-2">
                <div className="text-muted-foreground text-xs">Suggestions created</div>
                <div className="font-mono text-lg">{tallies.suggestions_created}</div>
              </div>
              <div className="border rounded p-2">
                <div className="text-muted-foreground text-xs">Confirmed</div>
                <div className="font-mono text-lg text-green-600">{tallies.confirmed}</div>
              </div>
              <div className="border rounded p-2">
                <div className="text-muted-foreground text-xs">Still unmatched</div>
                <div className="font-mono text-lg">{tallies.still_unmatched}</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
